/**
 * useAdminUsersGraphQL Hook
 *
 * Apollo Client hooks for admin user management queries.
 * Replaces TanStack Query hooks from useAdminUsers.ts.
 *
 * Feature: 065-graphql-migration-cleanup
 */

import { useQuery, gql } from '@apollo/client';

/**
 * GraphQL Queries
 */
const ADMIN_USERS_QUERY = gql`
  query AdminUsers($page: Int, $limit: Int, $filters: AdminUserFiltersInput) {
    adminUsers(page: $page, limit: $limit, filters: $filters) {
      users {
        id
        globalId
        username
        email
        displayName
        slug
        emailVerified
        isTestUser
        isAdmin
        createdAt
        updatedAt
        lastLoginAt
      }
      total
      page
      limit
    }
  }
`;

const ADMIN_USER_STATS_QUERY = gql`
  query AdminUserStats($filters: AdminUserFiltersInput) {
    adminUserStats(filters: $filters) {
      total
      verified
      unverified
      active7d
      active30d
      active90d
      inactive
      testUsers
      avgPhotos
      avgCollections
    }
  }
`;

/**
 * Filter types matching the REST API
 */
export interface AdminUserFilters {
  search?: string;
  verified?: boolean;
  active?: '7d' | '30d' | '90d';
  testUsers?: boolean;
  aiUsers?: boolean;
  adminUsers?: boolean;
  createdAfter?: string;
  createdBefore?: string;
}

export interface AdminUser {
  id: string;
  globalId: string;
  username: string;
  email: string;
  displayName: string;
  slug: string;
  emailVerified: boolean;
  isTestUser: boolean;
  isAdmin: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
}

export interface AdminUserStats {
  total: number;
  verified: number;
  unverified: number;
  active7d: number;
  active30d: number;
  active90d: number;
  inactive: number;
  testUsers: number;
  avgPhotos: number;
  avgCollections: number;
}

/**
 * Hook: Fetch paginated list of users with filters
 */
export function useAdminUsersGraphQL(
  page: number = 1,
  limit: number = 50,
  filters?: AdminUserFilters
) {
  const { data, loading, error, refetch } = useQuery<{
    adminUsers: {
      users: AdminUser[];
      total: number;
      page: number;
      limit: number;
    };
  }>(ADMIN_USERS_QUERY, {
    variables: {
      page,
      limit,
      filters: filters || {},
    },
    fetchPolicy: 'cache-and-network',
  });

  return {
    data: data?.adminUsers
      ? {
          users: data.adminUsers.users,
          total: data.adminUsers.total,
          page: data.adminUsers.page,
          limit: data.adminUsers.limit,
        }
      : undefined,
    loading,
    error,
    refetch,
    // Aliases to match TanStack Query API
    isLoading: loading,
    isError: !!error,
  };
}

/**
 * Hook: Fetch user statistics
 */
export function useAdminUserStatsGraphQL(filters?: AdminUserFilters) {
  const { data, loading, error, refetch } = useQuery<{
    adminUserStats: AdminUserStats;
  }>(ADMIN_USER_STATS_QUERY, {
    variables: {
      filters: filters || {},
    },
    fetchPolicy: 'cache-and-network',
  });

  return {
    data: data?.adminUserStats,
    loading,
    error,
    refetch,
    // Aliases to match TanStack Query API
    isLoading: loading,
    isError: !!error,
  };
}

// Re-export mutations from the existing GraphQL mutations hook
export {
  useAdminCreateUserGraphQL,
  useAdminGenerateTestUserGraphQL,
  useAdminUpdateUserGraphQL,
  useAdminVerifyUserGraphQL,
  useAdminUnverifyUserGraphQL,
  useAdminSendPasswordResetGraphQL,
  useAdminDeleteUserGraphQL,
} from '../../hooks/graphql/useAdminMutationsGraphQL';
